"use client";
import { Button } from "@repo/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@repo/ui";
import { ArrowUpRight, Menu, X } from "lucide-react";
import Link from "next/link";
import React from "react";

import { DocsDropdown } from "@/components/dashboard/docs-dropdown";
import { UserNav } from "@/components/dashboard/user-nav";
import NotificationDropdown from "@/components/ui/notification-dropdown";
import { Wordmark } from "@/components/wordmark";
import { useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import type { WhatsNewNavItem } from "@/lib/whats-new";

import { MarketingNavMenu } from "./marketing-nav-menu";
import { DEMO_BOOKING_URL, mobileNavLinks } from "./nav-links";
import { Container } from "./ui/container";

export default function HeaderClient({
  whatsNew,
}: {
  whatsNew: WhatsNewNavItem | null;
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);

  const { data: session } = useSession();
  const isSignedIn = Boolean(session);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <header
      role="banner"
      data-state={isMobileMenuOpen ? "active" : "inactive"}
      {...(isScrolled && { "data-scrolled": true })}
    >
      <div
        className={cn(
          "bg-background fixed inset-x-0 z-50 in-data-scrolled:border-b",
          "max-lg:border-b",
          // Collapse classes must not coexist with h-screen: in the docs route
          // group, fumadocs CSS layers reorder the cascade so max-lg:h-14 wins
          // over h-screen and clips the open mobile menu to a 56px strip. Gate
          // them on the closed state so the open menu always gets full height.
          isMobileMenuOpen
            ? "bg-background/75 h-screen backdrop-blur"
            : "max-lg:h-14 max-lg:overflow-hidden",
        )}
        style={{
          top: "var(--impersonation-banner-height, 0px)",
        }}
      >
        <Container>
          <div className="relative flex flex-wrap items-center justify-between lg:py-4">
            <div className="flex justify-between gap-4 max-lg:h-14 max-lg:w-full max-lg:border-b">
              <div className="flex items-center gap-2">
                <Link
                  href="/"
                  aria-label="home"
                  className="text-foreground flex items-center space-x-2"
                >
                  <Wordmark height={28} />
                </Link>
              </div>

              <MarketingNavMenu whatsNew={whatsNew} />
              <button
                onClick={() => {
                  setIsMobileMenuOpen(!isMobileMenuOpen);
                }}
                aria-label={isMobileMenuOpen ? "Close Menu" : "Open Menu"}
                className="relative z-20 -m-2.5 -mr-3 block cursor-pointer p-2.5 lg:hidden"
              >
                <Menu className="m-auto size-5 duration-5 ease-in-out in-data-[state=active]:scale-0 in-data-[state=active]:rotate-180 in-data-[state=active]:opacity-0" />
                <X className="absolute inset-0 m-auto size-5 scale-0 -rotate-180 opacity-0 duration-5 ease-in-out in-data-[state=active]:scale-100 in-data-[state=active]:rotate-0 in-data-[state=active]:opacity-100" />
              </button>
            </div>

            {isMobileMenuOpen && (
              <div className="w-full lg:hidden">
                <MobileMenu
                  closeMenu={() => {
                    setIsMobileMenuOpen(false);
                  }}
                />
              </div>
            )}

            <div className="mb-6 hidden w-full flex-wrap items-center justify-end space-y-8 in-data-[state=active]:flex max-lg:in-data-[state=active]:mt-6 md:flex-nowrap lg:m-0 lg:flex lg:w-fit lg:gap-6 lg:space-y-0 lg:border-transparent lg:bg-transparent lg:p-0 lg:shadow-none dark:shadow-none dark:lg:bg-transparent">
              <div className="flex w-full flex-col space-y-3 sm:flex-row sm:gap-3 sm:space-y-0 md:w-fit">
                {isSignedIn ? (
                  <nav className="flex items-center gap-2">
                    <NotificationDropdown />
                    <DocsDropdown className="hidden md:flex" />
                    <UserNav />
                  </nav>
                ) : (
                  <>
                    {/* Desktop buttons */}
                    <Button
                      asChild
                      variant="secondary"
                      className="hidden items-center gap-2 rounded-full sm:flex"
                    >
                      <Link href={DEMO_BOOKING_URL}>Book a Demo</Link>
                    </Button>
                    <Button
                      asChild
                      variant="default"
                      className="hidden items-center gap-2 rounded-full sm:flex"
                    >
                      <Link href="/login">
                        Sign in
                        <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    </Button>
                    {/* Mobile buttons */}
                    <Button
                      asChild
                      variant="secondary"
                      size="sm"
                      className="flex items-center gap-2 rounded-full sm:hidden"
                    >
                      <Link href={DEMO_BOOKING_URL}>Demo</Link>
                    </Button>
                    <Button
                      asChild
                      variant="default"
                      size="sm"
                      className="flex items-center gap-2 rounded-full sm:hidden"
                    >
                      <Link href="/login">
                        Sign in
                        <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </Container>
      </div>
    </header>
  );
}

const MobileMenu = ({ closeMenu }: { closeMenu: () => void }) => {
  return (
    <nav role="navigation" className="w-full">
      <Accordion
        type="single"
        collapsible
        className="-mx-4 mt-0.5 space-y-0.5 **:hover:no-underline"
      >
        {mobileNavLinks.map((group) => (
          <AccordionItem
            key={group.groupName}
            value={group.groupName}
            className="group relative border-b-0 before:pointer-events-none before:absolute before:inset-x-4 before:bottom-0 before:border-b"
          >
            <AccordionTrigger className="data-[state=open]:bg-muted flex items-center justify-between px-4 py-3 text-lg **:!font-normal">
              {group.groupName}
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <ul>
                {group.links.map((feature) => (
                  <li key={feature.href}>
                    <Link
                      href={feature.href}
                      onClick={closeMenu}
                      className="block px-4 py-2"
                    >
                      <div className="text-base">{feature.name}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </nav>
  );
};
